import { TableRow } from "@carbon6/c6-frontend-design";
import type { SessionCheckpointView, TrainingEvalRunSummaryView } from "@/api";
import type { CheckpointTag } from "@/modules/training/public";
import type { CheckpointAnalysisRow, CheckpointLossJoin } from "./analysis/sessionAnalysisModel";
import {
  artifactPathTail,
  checkpointDisplayLabel,
  checkpointRowId,
  checkpointSizeLabel,
  formatMetricNumber,
} from "./trainingSessionDetailFormat";

// 체크포인트 선택 표의 행 모델. 값(라벨·손실·크기)은 셀 문자열로 만들어 넣고, 평가 run 은 원본
// 목록으로 보관한다. 성공률 셀이 run 수·평균·시나리오 툴팁까지 한 셀에서 그린다.
export class CheckpointTableRow extends TableRow {
  // 체크포인트 태그 원문(`epoch_010`). 다운로드 콜백이 받는 키다. 태그 없는 산출물(카탈로그 등록
  // 정책의 artifact 가 태그 디렉터리 밖)은 null 이고, 그 행에는 다운로드 경로가 없다.
  readonly epoch: string | null;
  // 행 id 이자 등록 pending 표기의 키. 태그를 쓰고, 없으면 artifact 위치를 쓴다.
  readonly rowId: string;
  readonly label: string;
  // 산출 출처(mlflow 등). 서버가 빈 값을 보내면 셀도 생략한다.
  readonly sourceLabel: string | null;
  // 태그 없는 행에서만 채운다. 라벨이 고정 문구라서 행을 구분하는 표기는 artifact 경로 끝부분이다.
  readonly artifactTail: string | null;
  readonly lossLabel: string;
  readonly evalRuns: readonly TrainingEvalRunSummaryView[];
  readonly sizeLabel: string;
  // 강조 판정(Policy 이름 링크가 가리킨 체크포인트)이 보는 파싱된 태그. 파싱할 수 없으면 null 이다.
  readonly tag: CheckpointTag | null;
  // Policy 등록은 아티팩트 위치를 그대로 전달해야 하므로 원본 뷰를 보관한다.
  readonly checkpoint: SessionCheckpointView;

  constructor(row: CheckpointAnalysisRow) {
    super();
    const { checkpoint } = row;
    this.checkpoint = checkpoint;
    this.epoch = checkpoint.epoch;
    this.rowId = checkpointRowId(checkpoint);
    this.label = checkpointDisplayLabel(checkpoint.epoch);
    this.sourceLabel = checkpoint.source;
    this.artifactTail =
      checkpoint.epoch === null && checkpoint.artifact_uri !== null
        ? artifactPathTail(checkpoint.artifact_uri)
        : null;
    this.lossLabel = lossCellLabel(row.loss);
    this.evalRuns = row.evalRuns;
    this.sizeLabel = checkpointSizeLabel(checkpoint.size_bytes);
    this.tag = row.tag;
  }

  getRowId(): string {
    return this.rowId;
  }

  // 세션이 산출한 체크포인트는 모두 표시한다. 이 표에는 목록을 좁히는 축이 없다.
  canShow(): boolean {
    return true;
  }

  // 선택 UI 가 없는 표다.
  canSelect(): boolean {
    return false;
  }
}

export function createCheckpointTableRows(
  rows: readonly CheckpointAnalysisRow[],
): CheckpointTableRow[] {
  return rows.map((row) => new CheckpointTableRow(row));
}

// 저장 시점 손실 표기. 정확히 일치하지 않으면 ≈ 를 붙여 가장 가까운 값임을 표시한다.
function lossCellLabel(loss: CheckpointLossJoin): string {
  if (loss.value === null) {
    return "—";
  }
  const formatted = formatMetricNumber(loss.value);
  return loss.approximate ? `≈ ${formatted}` : formatted;
}
